"use client";

import React from "react";
import { motion, Variants } from "framer-motion";
import { ShieldCheck, Calendar, EyeOff, Lock, Database, Mail, Cookie, Globe, UserCheck, FileWarning } from "lucide-react";
import Link from "next/link";

export function PrivacyContent() {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const sections = [
    {
      icon: EyeOff,
      title: "1. Information We Collect",
      text: "CCMT College Finder is designed to be fully usable without requiring you to create an account or provide personal details such as your name, phone number, or home address. However, if you choose to use our Contact Us form, you voluntarily provide your name, email address, and message content. This information is stored securely in our Firestore database solely for the purpose of responding to your inquiry. We do not sell, trade, or transfer this information to third parties.",
    },
    {
      icon: Database,
      title: "2. Browser Local Storage & Session Data",
      text: "When you use the College Predictor tool, we save your preferences (such as your GATE paper code, GATE score, reservation category, and light/dark theme preference) directly inside your web browser using the browser's localStorage API. This data is stored strictly on your local device and is never transmitted to our servers. It exists purely for your convenience, so you do not need to re-enter your preferences on every page reload. You can clear this data at any time through your browser's settings.",
    },
    {
      icon: Cookie,
      title: "3. Cookies & Third-Party Advertising (Google AdSense)",
      text: "This website serves advertisements through Google AdSense to support its free operation. Google, as a third-party advertising vendor, uses cookies — including the DoubleClick DART cookie — to serve ads based on your prior visits to this website and other websites on the internet. These cookies allow Google and its partners to serve ads relevant to your interests. You may opt out of personalized advertising by visiting Google's Ads Settings at https://www.google.com/settings/ads, or by visiting the Network Advertising Initiative opt-out page at http://optout.networkadvertising.org/. Google's use of advertising cookies enables it and its partners to serve ads to users based on their visits to this site and/or other sites on the internet.",
    },
    {
      icon: ShieldCheck,
      title: "4. Google Analytics & Aggregated Analytics",
      text: "We use Google Analytics (Universal Analytics / GA4) to collect anonymized and aggregated usage data, such as page views, session durations, load times, device types, and geographic regions. This data helps us understand how users interact with our platform so we can improve performance and user experience. Google Analytics data does not contain personally identifiable information (PII). Google Analytics may also set its own cookies to measure user behavior. You can opt out of Google Analytics tracking by installing the Google Analytics Opt-out Browser Add-on at https://tools.google.com/dlpage/gaoptout.",
    },
    {
      icon: UserCheck,
      title: "5. Your Rights (GDPR, CCPA & General Data Rights)",
      text: "Depending on your location, you may have certain rights regarding your personal data. These may include: (a) The right to access the personal data we hold about you. (b) The right to request correction or deletion of your personal data. (c) The right to object to or restrict processing of your data. (d) The right to data portability. (e) For California residents under CCPA: the right to know what personal information is collected, the right to opt out of the sale of personal information (we do not sell personal information), and the right to non-discrimination for exercising your rights. To exercise any of these rights, please contact us at py.assistance@hotmail.com.",
    },
    {
      icon: Globe,
      title: "6. Third-Party Links & External Sites",
      text: "Our website contains links to external websites, including the official CCMT portal (ccmt.admissions.nic.in), NIRF rankings portal, and other educational resources. We are not responsible for the privacy practices or content of those external sites. We encourage you to review the privacy policy of every website you visit. This privacy policy applies solely to information collected by CCMT College Finder.",
    },
    {
      icon: Lock,
      title: "7. Security of Information",
      text: "We take reasonable precautions to protect against unauthorized access, disclosure, alteration, or destruction of the limited personal data we collect. Contact form submissions are stored in Google's Firebase Firestore with security rules restricting write access to authenticated server contexts. Our website is served exclusively over HTTPS to encrypt data in transit. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.",
    },
    {
      icon: FileWarning,
      title: "8. Children's Privacy",
      text: "This website is intended for users who are 18 years of age or older (primarily college students and graduates). We do not knowingly collect personal information from children under 13 years of age. If you believe that a child under 13 has provided personal information to us, please contact us immediately at py.assistance@hotmail.com so we can promptly delete the information.",
    },
    {
      icon: Calendar,
      title: "9. Changes to This Privacy Policy",
      text: "We may update this Privacy Policy periodically to reflect changes in our practices, technology, legal requirements, or for other operational reasons. We will post the updated policy on this page with a revised 'Last Updated' date. We encourage you to review this page periodically to stay informed about our privacy practices. Your continued use of CCMT College Finder after any changes to this policy constitutes your acceptance of the revised terms.",
    },
  ];

  return (
    <div className="min-h-screen bg-background selection:bg-primary/30 pt-10 pb-20 overflow-hidden">
      {/* Decorative Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full -z-10 pointer-events-none">
        <div className="absolute top-[-5%] right-[-10%] w-[50%] h-[40%] bg-primary/10 blur-[130px] rounded-full opacity-60" />
        <div className="absolute bottom-[10%] left-[-10%] w-[50%] h-[40%] bg-secondary/10 blur-[130px] rounded-full opacity-60" />
      </div>

      <div className="container mx-auto px-6 max-w-4xl">
        {/* Hero Section */}
        <section className="text-center py-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-surface-container/50 border border-outline-variant/10 text-primary text-sm font-bold mb-6 shadow-sm backdrop-blur-md"
          >
            <ShieldCheck className="size-4 text-emerald-500" />
            <span>Privacy & Safety</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground mb-6 leading-tight"
          >
            Privacy Policy
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-on-surface-variant bg-surface-container-low px-3 py-1 rounded-full border border-outline-variant/10"
          >
            <Calendar className="size-3.5" />
            <span>Last Updated: June 12, 2026</span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-on-surface-variant text-sm md:text-base max-w-2xl mx-auto mt-6 leading-relaxed"
          >
            This Privacy Policy describes how CCMT College Finder (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses, and shares information when you use our website. By accessing or using our service, you acknowledge that you have read and understood this policy.
          </motion.p>
        </section>

        {/* Policy Body */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="p-6 md:p-8 rounded-3xl bg-surface-container border border-outline-variant/20 shadow-sm flex flex-col md:flex-row gap-6 items-start"
              >
                <div className="flex-shrink-0 size-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="size-6" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-display font-bold text-lg md:text-xl text-foreground">
                    {section.title}
                  </h2>
                  <p className="text-on-surface-variant text-sm md:text-base leading-relaxed text-pretty">
                    {section.text}
                  </p>
                </div>
              </motion.div>
            );
          })}

          {/* Policy Inquiries Card */}
          <motion.div
            variants={itemVariants}
            className="p-6 md:p-8 rounded-3xl bg-primary/5 border border-primary/20 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6"
          >
            <div className="space-y-2 text-center md:text-left">
              <h2 className="font-display font-bold text-lg md:text-xl text-foreground flex items-center justify-center md:justify-start gap-2">
                <Mail className="size-5 text-primary" />
                Privacy Questions or Requests?
              </h2>
              <p className="text-on-surface-variant text-sm max-w-lg">
                If you have questions about this privacy policy, want to exercise your data rights, or have concerns about our data practices, please contact us directly. We are committed to addressing your concerns in a timely manner.
              </p>
              <p className="text-on-surface-variant text-xs font-semibold">
                Operated by: Piyush Prajapati & Ankit Yadav | India
              </p>
            </div>
            <Link
              href="mailto:py.assistance@hotmail.com"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:scale-105 active:scale-95 transition-all shadow-md whitespace-nowrap"
            >
              <span>Email: py.assistance@hotmail.com</span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
